import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, Grid, Snackbar, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Fragment, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import ApplicationsFormTtpfPurchaser from './applications-form-ttpf-purchaser';
import { CmsArticles, fetchCmsArticle } from '../../../../shared/helpers/cms-articles';
import { CaptureSignature } from '../../../components/CaptureSignature/capture-signature';
import { ThirdPartyCard } from '../../../components/ThirdPartyGrid/third-party-card';
import ThirdPartyGrid from '../../../components/ThirdPartyGrid/third-party-grid';
import {
  addPurchaserFailure,
  applicationSetField,
  resendEmailAsync,
  resendEmailClose,
  saveProfileFailure,
  saveSignatureAsync,
  saveSignatureFailure
} from '../../../redux/scholarship.actions';
import { JoiAddPurchaser } from '../../../redux/scholarship.joi';
import { EnumAppSignatureFormat, EnumAppSignatureType, EnumSections, MAppSignature, MProfile } from '../../../redux/scholarship.models';
import {
  selectProfilesParent,
  selectResendEmailStatus,
  selectSignatureParent,
  selectSignaturePurchaser
} from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    title: {
      fontWeight: 800
    },
    buttonSmall: {
      marginRight: theme.spacing(1),
      color: 'black',
      backgroundColor: '#e3e7ea',
      minWidth: '140px',
      width: 'auto',
      height: '30px',
      letterSpacing: '0.5px',
      lineHeight: '30px',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    rootLeft: {
      width: '100%',
      textAlign: 'center'
    }
  })
);

const getNewSignature = () => ({
  firstName: null,
  lastName: null,
  signatureFormat: null,
  signatureId: '',
  signatureType: EnumAppSignatureType.PARENT,
  signed: false,
  signedAgreementWording: ''
});

const ApplicationsFormTtpf = ({
  id,
  thisSection,
  hideSaveButtons,
  dispatchApplicationUpdate,
  dispatchResendEmail,
  dispatchResendEmailClose,
  dispatchSavePurchaserAsync
}) => {
  const [addMode, setAddMode] = useState<boolean>(false);
  const [signature, setSignature] = useState<MAppSignature>(getNewSignature());
  const [purchaserCertification, setPurchaserCertification] = useState(localStorage.getItem('purchaserCertification') || '');
  const [nonPurchaserCertification, setNonPurchaserCertification] = useState(localStorage.getItem('nonPurchaserCertification') || '');
  const [purchaserLegalText, setPurchaserLegalText] = useState('');
  const parentSignatures = useSelector<unknown, MAppSignature[]>(selectSignatureParent as (state: unknown) => MAppSignature[]);
  const profilesParent = useSelector<unknown, MProfile[]>(selectProfilesParent as (state: unknown) => MProfile[]);
  const purchaserSignature = useSelector<unknown, MAppSignature | undefined>(
    selectSignaturePurchaser as (state: unknown) => MAppSignature | undefined
  );
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const resendEmailStatus = useSelector(selectResendEmailStatus);
  const { trackPageView } = useMatomo();
  const dispatch = useDispatch();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    if (!purchaserSignature) {
      return;
    }

    const purchaserFirstName = `${purchaserSignature.firstName || ''}`;
    const purchaserLastName = `${purchaserSignature.lastName || ''}`;

    if (thisSection.notParentTtpfAccount) {
      if (thisSection.ttpfOwnerFirstName !== purchaserFirstName) {
        dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerFirstName', purchaserFirstName);
      }

      if (thisSection.ttpfOwnerLastName !== purchaserLastName) {
        dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerLastName', purchaserLastName);
      }

      return;
    }

    if (thisSection.notParentTtpfAccount == null) {
      if (thisSection.ttpfOwnerFirstName !== purchaserFirstName) {
        dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerFirstName', purchaserFirstName);
      }

      if (thisSection.ttpfOwnerLastName !== purchaserLastName) {
        dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerLastName', purchaserLastName);
      }

      dispatchApplicationUpdate(id, EnumSections.TTPF, 'notParentTtpfAccount', true);

      const purchaserSignatureWithWording = {
        ...purchaserSignature,
        signedAgreementWording: purchaserLegalText
      };
      dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfPurchaserSignature', purchaserSignatureWithWording);
    }
  }, [
    dispatchApplicationUpdate,
    id,
    purchaserLegalText,
    purchaserSignature,
    thisSection.notParentTtpfAccount,
    thisSection.ttpfOwnerFirstName,
    thisSection.ttpfOwnerLastName
  ]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const purchaserCertificationHTML = await fetchCmsArticle(CmsArticles.PURCHASER_CERTIFICATION);
        const nonPurchaserCertificationHTML = await fetchCmsArticle(CmsArticles.NON_PURCHASER_CERTIFICATION);
        const purchaserLegalTextHTML = await fetchCmsArticle(CmsArticles.PURCHASER_LEGAL_TEXT);
        setPurchaserCertification(purchaserCertificationHTML);
        setNonPurchaserCertification(nonPurchaserCertificationHTML);
        setPurchaserLegalText(purchaserLegalTextHTML);
        localStorage.setItem('purchaserCertification', purchaserCertification || '');
        localStorage.setItem('nonPurchaserCertification', nonPurchaserCertification || '');
      } catch (e) {
        console.error(e);
        setNonPurchaserCertification('Error loading.');
        setPurchaserCertification('Error loading.');
      }
    };

    void fetchArticle();
  }, []);

  useEffect(() => {
    return () => {
      dispatch(saveProfileFailure(null));
      dispatch(addPurchaserFailure(null));
      dispatch(saveSignatureFailure(null));
    };
  }, [dispatch]);

  const handleClose = () => {
    dispatchResendEmailClose();
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value === 'f') {
      dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerFirstName', `${profilesParent[0].firstName}`);
      dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfOwnerLastName', `${profilesParent[0].lastName}`);
    }
    dispatchApplicationUpdate(id, EnumSections.TTPF, 'notParentTtpfAccount', e.target.value === 't');
  };

  const handleToggle = (status: boolean): void => {
    if (status) {
      dispatch(saveSignatureFailure(null));
    }
    setAddMode(status);
    hideSaveButtons(status);
  };

  const handleEdit = (signatureId: string): void => {
    if (signatureId === 'new') {
      setSignature(getNewSignature());
    } else {
      setSignature(purchaserSignature ?? getNewSignature());
    }
    handleToggle(true);
    hideSaveButtons(true);
  };

  const handleSignature = (appId: string, sig: MAppSignature): void => {
    dispatchSavePurchaserAsync(appId, sig);
  };

  const changeCallbackSignature = (value: {
    firstName: string;
    lastName: string;
    checked: boolean;
    signed: boolean;
    signedAgreementWording: string;
    signatureType?: string;
    signatureFormat?: string;
  }): void => {
    const updatedSignature: Partial<MAppSignature> = {
      firstName: value.firstName,
      lastName: value.lastName,
      signed: value.signed,
      signedAgreementWording: nonPurchaserCertification,
      signatureType: value.signatureType as EnumAppSignatureType | null,
      signatureFormat: value.signatureFormat as EnumAppSignatureFormat | null
    };
    dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfParentSignature', updatedSignature);
  };

  const resendEmail = (row: MAppSignature): void => {
    dispatchResendEmail(row);
  };

  const renderTTPF = () => (
    <Fragment>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          TTPF Contract Information
        </Typography>
        <Typography variant="body2">
          Provide the Texas Tuition Promise Fund account number that is in your child’s name and you would like linked to his/her
          scholarship or grant if approved. This is the contract that will be used to determine if the minimum contribution requirement of
          the Match the Promise program is met.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          required
          id="ttpfId"
          name="ttpfId"
          label="Account Number"
          fullWidth
          inputProps={{ maxLength: 10 }}
          value={thisSection.ttpfId || ''}
          onChange={(event) => {
            dispatchApplicationUpdate(id, EnumSections.TTPF, 'ttpfId', event.target.value);
            dispatch(addPurchaserFailure(null));
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <RadioGroup
          aria-label="notParentTtpfAccount"
          name="notParentTtpfAccount"
          value={typeof thisSection.notParentTtpfAccount === 'boolean' ? (thisSection.notParentTtpfAccount ? 't' : 'f') : ''}
          onChange={(e) => {
            handleRadioChange(e);
            dispatch(addPurchaserFailure(null));
          }}
          row
        >
          <FormControlLabel
            key={`notParentTtpfAccount_false`}
            value={'f'}
            control={<Radio />}
            label={<Typography variant="body2">I am the purchaser of my child's Texas Tuition Promise Fund contract</Typography>}
          />
          <FormControlLabel
            key={`notParentTtpfAccount_true`}
            value={'t'}
            control={<Radio />}
            label={
              <Typography variant="body2">
                I am <strong>NOT</strong> the purchaser of my child's Texas Tuition Promise Fund contract
              </Typography>
            }
          />
        </RadioGroup>
      </Grid>
    </Fragment>
  );

  const renderNotParent = () => (
    <Grid item xs={12}>
      <Box marginBottom={2}>
        <div dangerouslySetInnerHTML={{ __html: nonPurchaserCertification }} />
        <CaptureSignature
          signedAgreementWording={nonPurchaserCertification}
          parentLabel="Parent/Guardian Signature"
          childLabel="Child Name"
          // disableNames={!!thisSection?.ttpfParentSignature?.firstName && !!thisSection?.ttpfParentSignature?.lastName}
          disableAll={
            !!thisSection?.ttpfParentSignature?.signed &&
            !!thisSection?.ttpfParentSignature?.firstName &&
            !!thisSection?.ttpfParentSignature?.lastName
          }
          initialFirstName={
            thisSection?.ttpfParentSignature?.firstName ? thisSection.ttpfParentSignature.firstName : parentSignatures[0].firstName
          }
          initialLastName={
            thisSection?.ttpfParentSignature?.lastName ? thisSection.ttpfParentSignature.lastName : parentSignatures[0].lastName
          }
          initialChildFirstName=""
          initialChildLastName=""
          initialChecked={!!(thisSection?.ttpfParentSignature?.signed || purchaserSignature?.signed)}
          initialSignatureType={EnumAppSignatureType.PARENT}
          initialSignatureFormat={EnumAppSignatureFormat.DIGITAL}
          changeCallback={changeCallbackSignature}
        />
      </Box>
      <Box>
        <div dangerouslySetInnerHTML={{ __html: purchaserCertification }} />
        <div className={classes.rootLeft}>
          {!purchaserSignature && (
            <Button
              variant="contained"
              color="secondary"
              className={classes.buttonSmall}
              onClick={() => {
                // Validate only the account number for Add Purchaser
                const validation = JoiAddPurchaser.validate({ ttpfId: thisSection.ttpfId });
                if (validation.error) {
                  dispatch(addPurchaserFailure(validation.error.details[0].message));

                  return;
                }
                dispatch(addPurchaserFailure(null));
                handleEdit('new');
              }}
              disabled={!thisSection.ttpfParentSignature?.signed}
            >
              Add Purchaser
            </Button>
          )}
        </div>
        {purchaserSignature ? (
          isMobile ? (
            <ThirdPartyCard
              signatures={[purchaserSignature]}
              handleEdit={handleEdit}
              resendEmail={resendEmail}
              handleSignature={handleSignature}
            />
          ) : (
            <ThirdPartyGrid
              signatures={[purchaserSignature]}
              handleEdit={handleEdit}
              resendEmail={resendEmail}
              handleSignature={handleSignature}
            />
          )
        ) : (
          <Fragment></Fragment>
        )}
      </Box>
      <Snackbar
        open={resendEmailStatus === 'success'}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity="success">
          Email re-sent successfully!
        </Alert>
      </Snackbar>
    </Grid>
  );

  return (
    <ApplicationFormContainer>
      {!addMode && (
        <Grid container spacing={3}>
          {renderTTPF()}
          {thisSection?.notParentTtpfAccount && renderNotParent()}
        </Grid>
      )}
      {addMode && <ApplicationsFormTtpfPurchaser id={id} signature={signature} handleToggle={handleToggle} />}
    </ApplicationFormContainer>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchApplicationUpdate: (id: string, section: string, field: string, value: unknown): void => {
    dispatch(applicationSetField(id, section, field, value));
  },
  dispatchResendEmail: (row: MAppSignature): void => {
    dispatch(resendEmailAsync(row));
  },
  dispatchResendEmailClose: (): void => {
    dispatch(resendEmailClose());
  },
  dispatchSavePurchaserAsync: (appId: string, mention: MAppSignature): void => {
    dispatch(saveSignatureAsync(appId, mention));
  }
});
export default connect(null, mapDispatchToProps)(ApplicationsFormTtpf);
